import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

// COMPONENTS
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import KogCheckbox from "@/main/components/shared/checkbox/kogCheckbox";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KOGModalConfirm from "@/main/components/shared/KogmodalConfirm/modalConfirm";

// SERVICES/MODELS/UTILS
import { ReassignPartsCancelledRequest, ReasonFilterRequest } from "@/main/models/request";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { convertStringForBoolean } from "@/main/services/utilsString";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getReason, useReasonReducer } from "@/main/store/reason";
import { KeyValueDefault } from "@/main/models/common";
import { ProcurementReassignToBodyShopResponse } from "@/main/models/response";
import { getDefaultCountryOptionDrop, getTranslateByKey, isEmpty, isNullUndefined } from "@/main/services/utils";
import { getPartTypesByDirectAssignCriteria } from "@/main/store/partType";
import { procurementApi } from "@/main/api";
import { getBrand, useBrandReducer } from "@/main/store/brand";
import { currencyToNumber } from "@/main/services/utilsNumber";
import { RoutesConstants } from "@/main/routes/routesConstants";

// STYLES & COMPONENTS LOCAL
import SectionParts from "./sectionParts/sectionParts";
import * as S from "./styles";
import { getRefreshForceUrl } from "@/main/services/utilsQueryParams";
interface IModalReassignBodyshopProps {
	isOpenModal: boolean;
	guidProcurement: string;
	guidQuote: string;
	claimNumber?: string;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	closeModal: () => void;
}
export type TypePartForm = {
	infoPartResponse: ProcurementReassignToBodyShopResponse;
	guidProcurementSupplierItem: string;
	price: string;
	partType: KeyValueDefault | null;
	partBrand: KeyValueDefault | null;
	isCheck: boolean;
};
const INITIAL_FORM_STATE = {
	parts: [] as TypePartForm[],
	partsSelected: [] as string[],
	reason: null as KeyValueDefault | null,
	reasonDescription: "",
};

export type TypeFormStateReassignBodyshop = typeof INITIAL_FORM_STATE;

function ModalReassignBodyshop({
	guidProcurement,
	guidQuote,
	isOpenModal,
	claimNumber,
	closeModal,
}: IModalReassignBodyshopProps) {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { countriesUser: countryReducer } = useCountryReducer();
	const { reason: reasonReducer } = useReasonReducer();
	const { brand: brandReducer } = useBrandReducer();

	const [isOpenModalConfirm, setIsOpenModalConfirm] = useState<boolean>(false);
	const [isLoadingGet, setIsLoadingGet] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

	const methods = useForm<TypeFormStateReassignBodyshop>({
		defaultValues: INITIAL_FORM_STATE,
	});
	const { control, getValues, setValue, reset, watch } = methods;
	const { fields } = useFieldArray({
		control,
		name: "parts",
	});
	const watcherIsCheck = useWatch({
		control,
		name: "partsSelected",
	});
	const allCheckboxesSelected = watcherIsCheck.length === fields.length;
	const watcherReason = watch("reason");
	const isReasonOther = convertStringForBoolean(watcherReason?.default);

	function handleClickSelectAll() {
		const guidsForm = getValues("parts").map((part: TypePartForm) => part.guidProcurementSupplierItem);
		const resultOptions = allCheckboxesSelected ? [] : guidsForm;
		guidsForm.forEach((guid: string) => {
			setValue(`parts.${guidsForm.indexOf(guid)}.isCheck`, !allCheckboxesSelected);
		});
		setValue("partsSelected", resultOptions);
	}

	async function loadParts() {
		setIsLoadingGet(true);
		procurementApi
			.getPartsToReassignToBodyShop({
				guidProcurement,
			})
			.then((response) => {
				const {
					data: { result },
				} = response;

				const parts: TypePartForm[] = result.map((part) => ({
					infoPartResponse: part,
					guidProcurementSupplierItem: part.guidProcurementSupplierItem,
					price: "",
					isCheck: !part.canceledBySupplier,
					partType: null,
					partBrand: null,
				}));

				const partsSelected = parts.filter((part) => part.isCheck).map((part) => part.guidProcurementSupplierItem);
				reset({
					...INITIAL_FORM_STATE,
					parts,
					partsSelected,
				});
			})
			.finally(() => {
				setIsLoadingGet(false);
			});
	}
	function validateForm(data: TypeFormStateReassignBodyshop) {
		if (isNullUndefined(data.reason)) {
			toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.reason").toLowerCase(),
				),
			);
			return false;
		}
		if (isEmpty(data.partsSelected)) {
			toast.error(
				getTranslateByKey("information.selectOne", true).replace(
					"{0}",
					getTranslateByKey("information.part").toLowerCase(),
				),
			);
			return false;
		}
		return true;
	}
	async function onSave() {
		const data = getValues();
		const partsSelected = data.parts.filter((part: TypePartForm) => part.isCheck);

		setIsLoadingSave(true);
		const request: ReassignPartsCancelledRequest[] = partsSelected.map((part: TypePartForm) => ({
			guidProcurementSupplierItem: part.guidProcurementSupplierItem,
			guidReason: String(data.reason?.key),
			reasonDescription: data.reasonDescription ? String(data.reasonDescription) : undefined,
			newPrice: part.price ? (currencyToNumber(part.price) ?? undefined) : undefined,
			idNewPartType: part.partType ? Number(part.partType.default) : undefined,
			guidNewPartBrand: part.partBrand ? String(part.partBrand.key) : undefined,
		}));

		procurementApi
			.reassignToBodyshopProcurementSupplierItems(request)
			.then(() => {
				const currentRoute = window.location.hash;
				const isInRouteOrderScrenn = currentRoute.includes(RoutesConstants.orderSearchOrder);
				const isInRouteSearchClaims = currentRoute.includes(RoutesConstants.claimSearchClaims);

				if (isInRouteOrderScrenn) {
					const filters = getRefreshForceUrl({
						quoteFilterVehicle: claimNumber,
					});
					const routeRedirect = `${RoutesConstants.orderSearchOrder}${filters}`;
					navigate(routeRedirect);
				} else if (isInRouteSearchClaims) {
					const filters = getRefreshForceUrl({
						claimNumber,
					});
					const routeRedirect = `${RoutesConstants.claimSearchClaims}${filters}`;
					navigate(routeRedirect);
				}

				closeModal();
				toast.success(getTranslateByKey("information.reassignCompletedSuccessfully"));
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}
	async function onSubmit(data: TypeFormStateReassignBodyshop) {
		if (!validateForm(data)) return;
		setIsOpenModalConfirm(true);
	}

	useEffect(() => {
		if (isOpenModal) {
			loadParts();
			dispatch(getPartTypesByDirectAssignCriteria({ guidQuote }));
			if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
			if (isNullUndefined(brandReducer.data)) dispatch(getBrand());
		}
	}, [isOpenModal]);

	useEffect(() => {
		const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
		if (isNullUndefined(countryUser)) return;

		const requestReason: ReasonFilterRequest = {
			guidCountry: String(countryUser.key),
			idReasonType: ReasonTypeEnum.Reassing,
		};
		dispatch(getReason(requestReason));
	}, [JSON.stringify(countryReducer.data)]);

	return (
		<FormProvider {...methods}>
			<form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
				<S.Container>
					<S.AreaHeader>
						<S.TitleModal>{getTranslateByKey("information.assignPartBodyshop")}</S.TitleModal>
						{!isLoadingGet && fields.length > 0 && (
							<KogCheckbox
								id={`id-searchOrder-checkbox-reassignBodyshop-selectAll`}
								disabled={fields.length === 0 || isLoadingGet}
								checked={allCheckboxesSelected}
								value={allCheckboxesSelected}
								onChange={() => {
									handleClickSelectAll();
								}}
							/>
						)}
					</S.AreaHeader>
					{isLoadingGet ? <LoadingComponent /> : <SectionParts />}
					<S.AreaFooter>
						{!isLoadingGet && fields.length > 0 && (
							<S.AreaReason>
								<KogAutoCompleteControlled
									id="id-searchOrder-select-reason"
									nameCustom="reason"
									isDefaultValueUnique
									label={getTranslateByKey("information.reason")}
									control={control}
									rulesCustom={{ required: true }}
									setValueCustom={setValue}
									options={reasonReducer.data}
									isLoading={reasonReducer.isLoading}
									onChangeCustom={(option) => {
										if (option === null) return;

										if (convertStringForBoolean((option as KeyValueDefault)?.default)) {
											setValue("reasonDescription", "");
										}
									}}
								/>
								{isReasonOther && (
									<KogInputTextControlledNew
										required={isReasonOther}
										id="id-searchOrder-input-description"
										label={getTranslateByKey("information.description")}
										name="reasonDescription"
										control={control}
									/>
								)}
							</S.AreaReason>
						)}
						<S.AreaButtons>
							<SecondaryButton type="button" onClick={() => closeModal()}>
								{getTranslateByKey("information.back")}
							</SecondaryButton>
							<Button
								type="submit"
								disabled={isLoadingSave || isLoadingGet || fields.length === 0}
								isLoading={isLoadingSave}
							>
								{getTranslateByKey("information.confirm")}
							</Button>
						</S.AreaButtons>
					</S.AreaFooter>
				</S.Container>
			</form>
			<KOGModalConfirm
				isOpenModal={isOpenModalConfirm}
				titleModal={getTranslateByKey("information.confirmReassignBodyshop")}
				setIsOpenModal={setIsOpenModalConfirm}
				closeModal={() => setIsOpenModalConfirm(false)}
				onCancel={() => setIsOpenModalConfirm(false)}
				onConfirm={() => onSave()}
			/>
		</FormProvider>
	);
}

function WrapperModalReassignBodyshop(props: IModalReassignBodyshopProps) {
	return (
		<KogModalSideComponent
			shouldRenderOpen
			title=""
			width="540px"
			open={props.isOpenModal}
			closeModal={() => props.setIsOpenModal(false)}
		>
			<ModalReassignBodyshop {...props} />
		</KogModalSideComponent>
	);
}

export default WrapperModalReassignBodyshop;
